const API_BASE = process.env.REACT_APP_API_BASE || '';

// Fully separate admin auth from the regular Supabase-backed user auth.
// Credentials are checked server-side against a single bcrypt hash (not
// Supabase Auth, not RLS) -- the admin panel talks to /api/admin/* using
// a Secure/HttpOnly/SameSite=Strict session cookie set by the backend
// (never readable from JS, unlike the old localStorage bearer token), and
// the backend uses the Supabase service role key to bypass RLS entirely
// for those endpoints. `credentials: 'include'` is required on every
// call so the browser attaches/receives that cookie.

let sessionActive = false;

export function hasAdminSession() {
  return sessionActive;
}

async function adminFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}/api/admin${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) {
    sessionActive = false;
    throw new Error('Admin session expired. Please log in again.');
  }
  if (res.status === 429) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Too many attempts. Try again later.');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Request failed (${res.status})`);
  }
  if (res.status === 204) return null;
  return res.json();
}

export async function adminLogin(username, password) {
  const res = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Invalid credentials');
  }
  const data = await res.json();
  sessionActive = true;
  return data;
}

export async function adminLogout() {
  try {
    await adminFetch('/logout', { method: 'POST' });
  } finally {
    sessionActive = false;
  }
}

export const adminGetMe = () => adminFetch('/me').then((data) => { sessionActive = true; return data; });
export const adminGetStats = () => adminFetch('/stats');
export const adminListUsers = (limit = 100, offset = 0) => adminFetch(`/users?limit=${limit}&offset=${offset}`);
export const adminDeleteUser = (id) => adminFetch(`/users/${id}`, { method: 'DELETE' });
export const adminListPosts = (limit = 100, offset = 0) => adminFetch(`/posts?limit=${limit}&offset=${offset}`);
export const adminDeletePost = (id) => adminFetch(`/posts/${id}`, { method: 'DELETE' });
export const adminListGalaxies = (limit = 100, offset = 0) => adminFetch(`/galaxies?limit=${limit}&offset=${offset}`);
export const adminDeleteGalaxy = (id) => adminFetch(`/galaxies/${id}`, { method: 'DELETE' });
